import { Badge, Button, Group, Modal, Paper, Skeleton, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { LegalDocumentSummary } from '@motionsafe/ms-console-api/admin/v1/legal_pb.js';
import { IconPlus, IconScale } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useId, useState, type FormEvent } from 'react';

import { api } from '../../api/client';
import { errorText, legalDocumentsQuery, meQuery, useInvalidate } from '../../api/hooks';
import { useDialog } from '../../components/dialog';
import { useGridRows } from '../../components/grid';
import { locked } from '../../components/locked';
import { EmptyState, PageHeader, ToolbarActions } from '../../components/page';
import { usePages } from '../../components/pager';
import { day, when } from '../../lib/format';
import { toDocumentKey } from '../../lib/legal';
import { isStaff } from '../../lib/roles';
import { ICON_INLINE } from '../../theme';
import { useAccess, useOrgId } from '../_app';
import { DialogLoadBar } from '../../components/top-load-bar';

// MotionSafe's legal documents, for MotionSafe staff: what everyone who signs
// in accepts, on a device or here, unless their organization's custom
// agreement covers them (ARCHITECTURE.md §16). Each row says which version
// people read now, which one they must have accepted, what is scheduled, and
// whether a draft is waiting. A row opens the document: its draft and every
// version. Writing them is a MotionSafe admin's; the rest of staff read.
export const Route = createFileRoute('/_app/legal-documents')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: LegalDocuments,
});

const COLUMNS = ['Document', 'In effect', 'Required', 'Scheduled', 'Draft'] as const;
const COLUMN_WIDTH: Partial<Record<(typeof COLUMNS)[number], number>> = { 'In effect': 210, Required: 150, Scheduled: 210, Draft: 200 };

function Dim({ children }: { children: string }) {
  return <Text span size="sm" c="dimmed">{children}</Text>;
}

function LegalDocuments() {
  const access = useAccess();
  const write = access.may('CreateLegalDocument');
  const captionId = useId();
  const orgId = useOrgId();
  const docs = useQuery(legalDocumentsQuery());
  const navigate = useNavigate();
  const creating = useDialog();
  const open = (documentId: string) => void navigate({ to: '/legal-documents/$documentId', params: { documentId }, search: { org: orgId } });
  // Active documents first, in the order sign-in shows them; retired ones after.
  const rows = [...(docs.data ?? [])].sort((a, b) => Number(a.status === 'retired') - Number(b.status === 'retired'));
  const pages = usePages(rows, 'documents');
  const gridRow = useGridRows(pages.shown.map((d) => d.id));

  const action = write
    ? <Button size="sm" leftSection={<IconPlus {...ICON_INLINE} />} onClick={creating.open}>New document</Button>
    : <Button size="sm" variant="default" leftSection={<IconPlus {...ICON_INLINE} />} {...locked(captionId)}>New document</Button>;

  return (
    <>
      <PageHeader
        title="Legal documents"
        description="What everyone who signs in accepts, on a device or here, unless their organization's custom agreement covers them. A new version is asked for at each person's next full sign-in."
      />

      <Group align="center" gap="sm" mb="md" wrap="wrap">
        <Text size="sm" c="dimmed">Every document applies to everyone. Nothing here is public: a person reads what they accepted under Your legal documents, in their account menu.</Text>
        <ToolbarActions note={write ? undefined : access.caption('CreateLegalDocument')} noteId={captionId}>{action}</ToolbarActions>
      </Group>

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {docs.data && rows.length === 0 ? (
          <EmptyState
            icon={IconScale}
            title="No documents yet"
            body="Nobody is asked to accept anything until a document's first version is published. A MotionSafe admin creates the first one here."
            {...(write ? { action: <Button variant="default" onClick={creating.open}>New document</Button> } : {})}
          />
        ) : (
          <Table.ScrollContainer minWidth={880}>
            <Table role="grid" layout="fixed" aria-label="Legal documents" aria-busy={docs.isPending}>
              <Table.Thead>
                <Table.Tr>{COLUMNS.map((c) => <Table.Th key={c} {...(COLUMN_WIDTH[c] ? { w: COLUMN_WIDTH[c] } : {})}>{c}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {docs.isPending && [0, 1].map((i) => (
                  <Table.Tr key={i} aria-hidden="true" bg="transparent">
                    <Table.Td><Skeleton height={14} width="55%" mb={8} /><Skeleton height={12} width="35%" /></Table.Td>
                    {[96, 72, 96, 96].map((w, j) => <Table.Td key={j}><Skeleton height={12} width={w} /></Table.Td>)}
                  </Table.Tr>
                ))}
                {pages.shown.map((d) => (
                  <Table.Tr key={d.id} {...gridRow(d.id, () => open(d.id))} onClick={() => open(d.id)} style={{ cursor: 'pointer', ...(d.status === 'retired' ? { opacity: 0.6 } : {}) }}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap" align="center">
                        <Text size="sm" fw={500} truncate>{d.title}</Text>
                        {d.status === 'retired' && <Badge size="sm" color="gray" style={{ flex: 'none' }}>retired</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed" className="id">{d.key}</Text>
                    </Table.Td>
                    <Table.Td><InEffect doc={d} /></Table.Td>
                    <Table.Td className="num">{d.requiredVersion ? <Text size="sm">Version {d.requiredVersion} or later</Text> : <Dim>nothing yet</Dim>}</Table.Td>
                    <Table.Td className="num">
                      {d.scheduled
                        ? <><Text size="sm">Version {d.scheduled.version}</Text><Text size="xs" c="dimmed">from {day(d.scheduled.effectiveAt)}</Text></>
                        : null}
                    </Table.Td>
                    <Table.Td className="num">
                      {d.draft
                        ? <><Text size="sm">Version {d.draft.version}</Text><Text size="xs" c="dimmed">saved {when(d.draft.updatedAt, { relative: true })}</Text></>
                        : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {pages.footer}
        {docs.isError && (
          <Group gap="sm" justify="center" py="xl" px="lg">
            <Text size="sm" c="danger">Could not load the documents: {errorText(docs.error)}.</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => void docs.refetch()} disabled={docs.isFetching}>Try again</Button>
          </Group>
        )}
      </Paper>

      {write && creating.mounted && <CreateDocumentDialog opened={creating.opened} round={creating.round} onClose={creating.close} onCreated={open} />}
    </>
  );
}

// The version people read now, and since when; none before the first is in effect.
function InEffect({ doc }: { doc: LegalDocumentSummary }) {
  const v = doc.inEffect;
  if (!v) return <Dim>{doc.status === 'retired' ? 'none' : 'none yet'}</Dim>;
  return (
    <>
      <Text size="sm" className="num">Version {v.version}</Text>
      <Text size="xs" c="dimmed" className="num">since {day(v.effectiveAt ?? v.publishedAt)}</Text>
    </>
  );
}

function CreateDocumentDialog({ opened, round, ...props }: { opened: boolean; round: number; onClose: () => void; onCreated: (documentId: string) => void }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title="New legal document" size={440}>
      <DialogLoadBar />
      <CreateDocumentForm key={round} {...props} />
    </Modal>
  );
}

function CreateDocumentForm({ onClose, onCreated }: { onClose: () => void; onCreated: (documentId: string) => void }) {
  const inv = useInvalidate();
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  // The key follows the title until the person types one of their own.
  const [keyTyped, setKeyTyped] = useState(false);
  const [error, setError] = useState('');
  // A refusal is shown under the field it is about.
  const about = /\bkey\b/.test(error) ? 'key' : /\btitle\b/.test(error) ? 'title' : 'form';
  const create = useMutation({
    mutationFn: () => api.legal.createLegalDocument({ key, title: title.trim() }),
    onSuccess: (r) => {
      void inv('legal');
      notifications.show({ message: `${title.trim()} created, with an empty draft. Write its text, then publish its first version.` });
      onClose();
      if (r.document) onCreated(r.document.id);
    },
    onError: (e) => setError(errorText(e)),
  });
  const ready = !!title.trim() && key.length >= 2 && !create.isPending;
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (ready) create.mutate(); }}>
      <Stack gap="lg">
        <TextInput
          label="Title"
          withAsterisk
          placeholder="Data Processing Addendum"
          value={title}
          onChange={(e) => { const v = e.currentTarget.value; setTitle(v); if (!keyTyped) setKey(toDocumentKey(v).replace(/_+$/, '')); if (about === 'title') setError(''); }}
          error={about === 'title' ? error : undefined}
          data-autofocus
        />
        <TextInput
          label="Key"
          withAsterisk
          description="How the audit trail names it. It cannot change."
          value={key}
          onChange={(e) => { setKeyTyped(true); setKey(toDocumentKey(e.currentTarget.value)); if (about === 'key') setError(''); }}
          error={about === 'key' ? error : undefined}
          autoComplete="off"
          spellCheck={false}
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
        />
        {about === 'form' && error && <Text size="sm" c="danger" role="alert">{error}</Text>}
        <Text size="sm" c="dimmed">Nobody is asked to accept it until its first version is published.</Text>
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button type="submit" disabled={!ready || create.isPending}>Create document</Button>
        </Group>
      </Stack>
    </form>
  );
}
